// Step 3 — Tax Matters (Part I, Line 3)
import React from 'react';
import { Plus, Trash2, ShieldPlus, Link2 } from 'lucide-react';
import type { POAFormState, POATaxMatter, StepErrors } from '../../../types/poa';
import { MATTER_DESCRIPTIONS, COMMON_TAX_FORMS, MAX_FUTURE_YEAR, MIN_YEAR, MAX_TAX_MATTERS } from '../poaConstants';
import { isCivilPenaltyMatter } from '../usePOAForm';

interface Props {
  form: POAFormState;
  errors: StepErrors;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: keyof POATaxMatter, value: string | number) => void;
  onAddCivilPenalty: () => void;
}

const inputStyle = (hasError?: boolean): React.CSSProperties => ({
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: `1px solid ${hasError ? 'var(--bad)' : 'var(--border)'}`,
  background: 'rgba(255,255,255,.04)', color: 'var(--text)',
  fontSize: 14, outline: 'none',
});

const selectStyle = (hasError?: boolean): React.CSSProperties => ({
  ...inputStyle(hasError),
  cursor: 'pointer',
  background: '#1e2030',
  colorScheme: 'dark',
});

const years = Array.from(
  { length: MAX_FUTURE_YEAR - MIN_YEAR + 1 },
  (_, i) => MAX_FUTURE_YEAR - i,
);

function MatterCard({
  matter, index, errors, total, onRemove, onUpdate,
}: {
  matter: POATaxMatter; index: number; errors: StepErrors;
  total: number; onRemove: () => void;
  onUpdate: (field: keyof POATaxMatter, value: string | number) => void;
}) {
  const p = `matter_${index}`;

  // Description of Matter — "Other" mode
  const isOtherDesc = matter.matterDescription === 'Other (specify)' ||
    (matter.matterDescription !== '' && !(MATTER_DESCRIPTIONS as readonly string[]).includes(matter.matterDescription));
  const descSelectValue = isOtherDesc ? 'Other (specify)' : matter.matterDescription;
  const descInputValue  = matter.matterDescription === 'Other (specify)' ? '' : matter.matterDescription;

  // Tax Form Number — "Other" mode
  const isOtherForm = matter.taxFormNumber === '__other__' ||
    (matter.taxFormNumber !== '' && !COMMON_TAX_FORMS.find(f => f.value === matter.taxFormNumber));
  const formSelectValue = isOtherForm ? '__other__' : matter.taxFormNumber;
  const formInputValue  = matter.taxFormNumber === '__other__' ? '' : matter.taxFormNumber;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="hd" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          Tax Matter #{index + 1}
          {isCivilPenaltyMatter(matter) && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
              background: 'rgba(158,206,106,.1)', color: '#9ece6a',
              border: '1px solid rgba(158,206,106,.3)',
            }}>
              Civil Penalty coverage
            </span>
          )}
          {index > 0 && matter.autoMirror && (
            <span
              title="These years track Matter #1. Change them here and they stop tracking."
              style={{
                fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
                background: 'rgba(122,162,255,.1)', color: 'var(--accent)',
                border: '1px solid rgba(122,162,255,.3)',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              <Link2 size={11} /> Years follow Matter #1
            </span>
          )}
        </span>
        {total > 1 && (
          <button
            className="btn"
            style={{ padding: '4px 8px', fontSize: 12, color: 'var(--bad)', borderColor: 'rgba(255,107,107,.3)' }}
            onClick={onRemove}
          >
            <Trash2 size={13} /> Remove
          </button>
        )}
      </div>
      <div className="bd">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Description of Matter */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
              Description of Matter<span style={{ color: 'var(--bad)', marginLeft: 3 }}>*</span>
            </label>
            <select style={selectStyle(!!errors[`${p}_desc`])} value={descSelectValue}
              onChange={(e) => onUpdate('matterDescription', e.target.value)}>
              <option value="">— select —</option>
              {MATTER_DESCRIPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {isOtherDesc && (
              <input style={{ ...inputStyle(), marginTop: 8 }}
                placeholder="Describe the matter"
                value={descInputValue}
                onChange={(e) => onUpdate('matterDescription', e.target.value)} />
            )}
            {errors[`${p}_desc`] && (
              <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 3 }}>{errors[`${p}_desc`]}</div>
            )}
          </div>

          {/* Tax Form Number */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
              Tax Form Number<span style={{ color: 'var(--bad)', marginLeft: 3 }}>*</span>
            </label>
            <select style={selectStyle(!!errors[`${p}_form`])} value={formSelectValue}
              onChange={(e) => onUpdate('taxFormNumber', e.target.value)}>
              <option value="">— select or type below —</option>
              {COMMON_TAX_FORMS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
              <option value="__other__">Other (enter below)</option>
            </select>
            {isOtherForm && (
              <input style={{ ...inputStyle(), marginTop: 8 }}
                placeholder="Enter form number (e.g. 5500)"
                value={formInputValue}
                onChange={(e) => onUpdate('taxFormNumber', e.target.value)} />
            )}
            {errors[`${p}_form`] && (
              <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 3 }}>{errors[`${p}_form`]}</div>
            )}
          </div>
        </div>

        {/* Period */}
        <div>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
            Year(s) or Period(s)<span style={{ color: 'var(--bad)', marginLeft: 3 }}>*</span>
            <span style={{ fontSize: 11, marginLeft: 8 }}>
              (IRS does not accept "All years" — you must specify exact years)
            </span>
          </label>

          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {(['year', 'range', 'specific'] as const).map((type) => (
              <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" name={`periodType_${matter.id}`} value={type}
                  checked={matter.periodType === type}
                  onChange={() => onUpdate('periodType', type)} />
                {type === 'year' ? 'Single Year' : type === 'range' ? 'Year Range' : 'Fiscal / Quarterly / Other'}
              </label>
            ))}
          </div>

          {matter.periodType === 'year' && (
            <select style={{ ...selectStyle(!!errors[`${p}_period`]), maxWidth: 180 }}
              value={matter.periodStart}
              onChange={(e) => onUpdate('periodStart', Number(e.target.value))}>
              <option value="">— select year —</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}

          {matter.periodType === 'range' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <select style={{ ...selectStyle(!!errors[`${p}_period`]), maxWidth: 160 }}
                value={matter.periodStart}
                onChange={(e) => onUpdate('periodStart', Number(e.target.value))}>
                <option value="">Start year</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <span style={{ color: 'var(--muted)' }}>through</span>
              <select style={{ ...selectStyle(!!errors[`${p}_period`]), maxWidth: 160 }}
                value={matter.periodEnd}
                onChange={(e) => onUpdate('periodEnd', Number(e.target.value))}>
                <option value="">End year</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}

          {matter.periodType === 'specific' && (
            <input style={{ ...inputStyle(!!errors[`${p}_period`]), maxWidth: 340 }}
              placeholder="e.g. Q2 2023–Q3 2024  or  01/01/2023–06/30/2023"
              value={matter.periodRaw}
              onChange={(e) => onUpdate('periodRaw', e.target.value)} />
          )}

          {errors[`${p}_period`] && (
            <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 5 }}>{errors[`${p}_period`]}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TaxMattersStep({ form, errors, onAdd, onRemove, onUpdate, onAddCivilPenalty }: Props) {
  const hasCivilPenalty = form.taxMatters.some(isCivilPenaltyMatter);
  const atCapacity      = form.taxMatters.length >= MAX_TAX_MATTERS;

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
        Specify each tax matter being authorized. Add a separate row for each combination of
        matter type, form number, and period — Line 3 of the form holds {MAX_TAX_MATTERS}.
        Future years are limited to 3 years beyond the current calendar year.
      </p>

      {errors._matters && (
        <div style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 12, padding: '8px 12px', border: '1px solid var(--bad)', borderRadius: 8, background: 'rgba(255,107,107,.06)' }}>
          {errors._matters}
        </div>
      )}

      {form.taxMatters.map((m, i) => (
        <MatterCard
          key={m.id}
          matter={m}
          index={i}
          errors={errors}
          total={form.taxMatters.length}
          onRemove={() => onRemove(m.id)}
          onUpdate={(field, value) => onUpdate(m.id, field, value)}
        />
      ))}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          className="btn"
          disabled={atCapacity}
          style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, opacity: atCapacity ? .45 : 1 }}
          onClick={onAdd}
          title={atCapacity ? `Form 2848 Line 3 holds ${MAX_TAX_MATTERS} matters` : undefined}
        >
          <Plus size={14} /> Add Tax Matter
        </button>
        {!hasCivilPenalty && !atCapacity && (
          <button
            className="btn"
            style={{
              fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
              color: '#9ece6a', borderColor: 'rgba(158,206,106,.35)',
              background: 'rgba(158,206,106,.07)',
            }}
            onClick={onAddCivilPenalty}
            title="Adds a Civil Penalty row covering the same years as Matter #1"
          >
            <ShieldPlus size={14} /> Add Civil Penalty coverage
          </button>
        )}
      </div>

      {!hasCivilPenalty && !atCapacity && (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10, lineHeight: 1.5, maxWidth: 640 }}>
          Carrying Civil Penalty coverage means a penalty question that comes up mid-call or
          mid-examination doesn't need a fresh Form 2848 from the client.
        </div>
      )}

      {atCapacity && (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10, lineHeight: 1.5, maxWidth: 640 }}>
          Line 3 of Form 2848 has {MAX_TAX_MATTERS} rows, so this is the maximum for one form.
          If the client needs broader coverage, prepare a second Form 2848.
        </div>
      )}
    </div>
  );
}
